'use client';

import Link from 'next/link';
import posthog from 'posthog-js';
import { motion } from 'framer-motion';
import { Flag, Loader2, MessageCircle, Send, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  COMMENT_ERROR_COPY,
  COMMENT_MAX_CHARS,
  canDeleteComment,
  classifyCommentError,
  commentLengthBucket,
  formatCount,
  mergeComment,
  normalizeCommentBody,
  type ClipComment,
} from '@/lib/clip-engagement';
import {
  COMMENTS_PAGE_SIZE,
  deleteClipComment,
  fetchClipComment,
  fetchClipComments,
  postClipComment,
  reportClipComment,
  subscribeToClipComments,
  type CommentReportReason,
} from '@/lib/clip-engagement-api';
import { getDisplayImageUrl } from '@/lib/image-upload';
import { timeAgo } from '@/lib/notifications';

interface ClipCommentsProps {
  clipId: string;
  clipOwnerId: string;
  viewerId: string | null;
  count: number;
  /** Comment count changed locally (+1 posted, -1 deleted) or was re-synced. */
  onCountChange: (delta: number) => void;
  onClose: () => void;
  isDesktop: boolean;
  reduceMotion: boolean;
}

type LoadState = 'loading' | 'ready' | 'error' | 'unavailable';

const REPORT_REASONS: { value: CommentReportReason; label: string }[] = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'inappropriate_content', label: 'Inappropriate' },
  { value: 'other', label: 'Other' },
];

/**
 * Comments for one clip: a right-hand panel on desktop, a bottom sheet on
 * mobile. Keys typed here never reach the viewer's shortcuts.
 */
export default function ClipComments({
  clipId,
  clipOwnerId,
  viewerId,
  count,
  onCountChange,
  onClose,
  isDesktop,
  reduceMotion,
}: ClipCommentsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [comments, setComments] = useState<ClipComment[]>([]);
  const commentsRef = useRef<ClipComment[]>([]);
  commentsRef.current = comments;
  const countRef = useRef(count);
  countRef.current = count;
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const load = useCallback(async () => {
    setState('loading');
    const res = await fetchClipComments(clipId);
    if (res.status !== 'ok') {
      setState(res.status);
      return;
    }
    setComments(res.comments);
    setState('ready');
    // A short list is the whole list: correct a drifted counter.
    if (res.comments.length < COMMENTS_PAGE_SIZE && res.comments.length !== countRef.current) {
      onCountChangeRef.current(res.comments.length - countRef.current);
    }
  }, [clipId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live: comments others post while the panel is open.
  useEffect(() => {
    return subscribeToClipComments(clipId, async (id) => {
      if (commentsRef.current.some((c) => c.id === id)) return;
      const comment = await fetchClipComment(id);
      if (!comment || commentsRef.current.some((c) => c.id === id)) return;
      setComments((prev) => mergeComment(prev, comment));
      onCountChangeRef.current(1);
    });
  }, [clipId]);

  const onPosted = (comment: ClipComment) => {
    if (commentsRef.current.some((c) => c.id === comment.id)) return;
    setComments((prev) => mergeComment(prev, comment));
    onCountChangeRef.current(1);
  };

  const onDelete = async (comment: ClipComment) => {
    const prev = commentsRef.current;
    setComments(prev.filter((c) => c.id !== comment.id));
    onCountChangeRef.current(-1);
    const ok = await deleteClipComment(comment.id);
    if (!ok) {
      setComments(prev);
      onCountChangeRef.current(1);
      return false;
    }
    posthog.capture('clip_comment_deleted', { platform: 'web', own: comment.user_id === viewerId });
    return true;
  };

  const variants = reduceMotion
    ? { hidden: { opacity: 0 }, shown: { opacity: 1 } }
    : isDesktop
      ? { hidden: { x: '100%' }, shown: { x: 0 } }
      : { hidden: { y: '100%' }, shown: { y: 0 } };

  return (
    <>
      {/* Mobile: tap above the sheet to close it. */}
      {!isDesktop && (
        <motion.div
          className="absolute inset-0 z-40 bg-black/40"
          aria-hidden
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
      <motion.section
        role="region"
        aria-label="Comments"
        className="absolute inset-x-0 bottom-0 z-50 flex h-[70dvh] flex-col overflow-hidden rounded-t-2xl border-t border-white/10 bg-neutral-950 text-white shadow-[0_-20px_60px_rgba(0,0,0,0.5)] md:inset-x-auto md:inset-y-0 md:right-0 md:h-full md:w-[400px] md:rounded-none md:border-l md:border-t-0 md:shadow-none"
        variants={variants}
        initial="hidden"
        animate="shown"
        exit="hidden"
        transition={{ duration: reduceMotion ? 0.15 : 0.28, ease: [0.32, 0.72, 0, 1] }}
        // The viewer navigates on wheel; scrolling the list must not.
        onWheel={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-white/10 py-1 pl-4 pr-1">
          <h2 className="text-sm font-semibold">
            Comments <span className="tabular-nums text-white/50">{formatCount(count)}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close comments"
            className="flex size-11 cursor-pointer items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain">
          {state === 'loading' && (
            <div className="flex h-full items-center justify-center text-white/50">
              <Loader2 size={20} className="animate-spin" aria-label="Loading comments" />
            </div>
          )}
          {state === 'error' && (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-white/60">
              <p>Couldn&apos;t load comments.</p>
              <button
                type="button"
                onClick={() => void load()}
                className="min-h-11 cursor-pointer rounded-full bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/20"
              >
                Retry
              </button>
            </div>
          )}
          {state === 'unavailable' && (
            <p className="flex h-full items-center justify-center px-6 text-center text-sm text-white/60">
              {COMMENT_ERROR_COPY.unavailable}
            </p>
          )}
          {state === 'ready' && comments.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <span className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <MessageCircle size={20} />
              </span>
              <p className="text-sm font-semibold">No comments yet</p>
              <p className="text-sm text-white/50">Be the first to say something.</p>
            </div>
          )}
          {state === 'ready' && comments.length > 0 && (
            <ul className="flex flex-col py-2">
              {comments.map((c) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  canDelete={canDeleteComment(c, viewerId, clipOwnerId)}
                  canReport={!!viewerId && c.user_id !== viewerId}
                  onDelete={() => onDelete(c)}
                />
              ))}
            </ul>
          )}
        </div>

        <div
          className="border-t border-white/10 p-3"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          {viewerId ? (
            <Composer clipId={clipId} viewerId={viewerId} onPosted={onPosted} onEscape={onClose} disabled={state === 'unavailable'} />
          ) : (
            <SignInPrompt text="Sign in to comment" />
          )}
        </div>
      </motion.section>
    </>
  );
}

export function SignInPrompt({ text }: { text: string }) {
  const [href, setHref] = useState('/login');
  useEffect(() => {
    setHref(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }, []);
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-white/70">{text}</p>
      <Link
        href={href}
        className="flex min-h-11 items-center rounded-full bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
      >
        Sign in
      </Link>
    </div>
  );
}

function CommentRow({
  comment,
  canDelete,
  canReport,
  onDelete,
}: {
  comment: ClipComment;
  canDelete: boolean;
  canReport: boolean;
  onDelete: () => Promise<boolean>;
}) {
  const [reporting, setReporting] = useState(false);
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'done' | 'failed'>('idle');
  const [deleteFailed, setDeleteFailed] = useState(false);
  const gamertag = comment.user?.gamertag ?? 'player';
  const profileHref = `/profile/${encodeURIComponent(gamertag)}`;
  const avatar = getDisplayImageUrl(comment.user?.profile_image_url ?? null, 64);

  const report = async (reason: CommentReportReason) => {
    setReportState('sending');
    const ok = await reportClipComment(comment.id, reason);
    setReportState(ok ? 'done' : 'failed');
    setReporting(false);
    if (ok) posthog.capture('clip_comment_reported', { platform: 'web', reason });
  };

  return (
    <li className="group flex gap-3 px-4 py-2.5">
      <Link href={profileHref} className="shrink-0" aria-label={`@${gamertag}'s profile`}>
        <Avatar className="size-9 bg-neutral-900">
          {avatar && <AvatarImage src={avatar} alt="" className="object-cover" />}
          <AvatarFallback className="bg-neutral-800 text-[10px] font-semibold text-white">
            {gamertag.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-xs">
          <Link href={profileHref} className="truncate font-semibold text-white/80 transition-colors hover:text-white">
            @{gamertag}
          </Link>
          <time dateTime={comment.created_at} className="shrink-0 tabular-nums text-white/40">
            {timeAgo(comment.created_at)}
          </time>
        </p>
        <p className="mt-0.5 whitespace-pre-line break-words text-sm text-white/90">{comment.body}</p>
        {reporting && (
          <div className="mt-1 flex flex-wrap items-center gap-1" role="group" aria-label="Report reason">
            {REPORT_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                disabled={reportState === 'sending'}
                onClick={() => void report(r.value)}
                className="min-h-11 cursor-pointer rounded-full bg-white/10 px-3 text-xs font-semibold text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
              >
                {r.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setReporting(false)}
              className="min-h-11 cursor-pointer px-2 text-xs text-white/50 transition-colors hover:text-white"
            >
              Cancel
            </button>
          </div>
        )}
        {reportState === 'done' && <p className="mt-1 text-xs text-white/50" role="status">Reported. Thanks.</p>}
        {reportState === 'failed' && <p className="mt-1 text-xs text-red-300" role="status">Couldn&apos;t report. Try again.</p>}
        {deleteFailed && <p className="mt-1 text-xs text-red-300" role="status">Couldn&apos;t delete. Try again.</p>}
      </div>
      <div className="-mr-2 flex shrink-0 items-start">
        {canReport && reportState !== 'done' && !reporting && (
          <button
            type="button"
            onClick={() => setReporting(true)}
            aria-label={`Report comment by @${gamertag}`}
            className="flex size-11 cursor-pointer items-center justify-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Flag size={15} />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={async () => {
              setDeleteFailed(false);
              const ok = await onDelete();
              if (!ok) setDeleteFailed(true);
            }}
            aria-label="Delete comment"
            className="flex size-11 cursor-pointer items-center justify-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </li>
  );
}

function Composer({
  clipId,
  viewerId,
  onPosted,
  onEscape,
  disabled,
}: {
  clipId: string;
  viewerId: string;
  onPosted: (c: ClipComment) => void;
  onEscape: () => void;
  disabled: boolean;
}) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  // Grow with the text, up to ~4 lines.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  }, [text]);

  const body = normalizeCommentBody(text);
  const remaining = COMMENT_MAX_CHARS - text.trim().length;

  const submit = async () => {
    if (!body || posting || disabled) return;
    setPosting(true);
    setError(null);
    const res = await postClipComment(clipId, viewerId, body);
    setPosting(false);
    if (!res.ok) {
      setError(COMMENT_ERROR_COPY[classifyCommentError(res.error)]);
      return;
    }
    setText('');
    onPosted(res.comment);
    posthog.capture('clip_comment_posted', { platform: 'web', length_bucket: commentLengthBucket(body.length) });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-end gap-2">
        <label htmlFor={`clip-comment-${clipId}`} className="sr-only">
          Add a comment
        </label>
        <textarea
          id={`clip-comment-${clipId}`}
          ref={ref}
          rows={1}
          value={text}
          maxLength={COMMENT_MAX_CHARS + 50}
          disabled={disabled}
          placeholder="Add a comment…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Keep the viewer's arrow / space / Esc shortcuts out of the
            // composer (Tab still reaches the dialog's focus trap).
            if (e.key !== 'Tab') e.stopPropagation();
            if (e.key === 'Escape') {
              e.preventDefault();
              onEscape();
            } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            }
          }}
          className="min-h-11 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-white/30 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!body || posting || disabled}
          aria-label="Post comment"
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-default disabled:bg-white/10 disabled:text-white/40"
        >
          {posting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
      <div className="mt-1 flex min-h-4 items-center justify-between px-1 text-xs">
        <span className="text-red-300" role="status" aria-live="polite">
          {error ?? ''}
        </span>
        {remaining <= 100 && (
          <span className={`tabular-nums ${remaining < 0 ? 'text-red-300' : 'text-white/40'}`}>{remaining}</span>
        )}
      </div>
    </form>
  );
}
